import { createClient } from '@supabase/supabase-js'

// Use environment variables with fallbacks for development
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Check if we have valid configuration
const isConfigured = supabaseUrl && 
  supabaseAnonKey && 
  supabaseUrl !== 'your_supabase_project_url' && 
  supabaseAnonKey !== 'your_supabase_anon_key' &&
  supabaseUrl.startsWith('https://') &&
  supabaseUrl.includes('.supabase.co')

// Export configuration status for use in other modules
export const isSupabaseConfigured = isConfigured

// Only create client if properly configured, otherwise use safe defaults
const safeSupabaseUrl = isConfigured ? supabaseUrl : 'https://placeholder.supabase.co'
const safeSupabaseAnonKey = isConfigured ? supabaseAnonKey : 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBsYWNlaG9sZGVyIiwicm9sZSI6ImFub24iLCJpYXQiOjE2NDUxOTI4MDAsImV4cCI6MTk2MDc2ODgwMH0.placeholder'

if (!isConfigured) {
  console.warn('⚠️ Supabase is not configured. Running in demo mode. Please click "Connect to Supabase" in the top right to set up your project.')
}

// Create a single supabase client for the entire app
export const supabase = createClient(safeSupabaseUrl, safeSupabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
  }
})

// Database types
export interface UserProfile {
  id: string
  email: string
  full_name: string
  avatar_url?: string
  department?: string
  role_id?: string
  is_active: boolean
  last_login?: string
  created_at: string
  updated_at: string
  role_name?: string
  role_level?: number
}

export interface Role {
  id: string
  name: string
  description?: string
  level: number
  created_at: string
}

export interface Permission {
  id: string
  name: string
  description?: string
  resource: string
  action: string
  created_at: string
}

// Auth helper functions with better error handling
export const signUp = async (email: string, password: string, fullName: string) => {
  if (!isConfigured) {
    return { 
      data: null, 
      error: { 
        message: 'Supabase is not configured. Please connect to Supabase first.',
        status: 400
      } 
    }
  }

  try {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
      },
    })
    return { data, error }
  } catch (error) {
    console.error('Network error during sign up:', error)
    return { 
      data: null, 
      error: { 
        message: 'Network error. Please check your connection and try again.',
        status: 500
      } 
    }
  }
}

export const signIn = async (email: string, password: string) => {
  if (!isConfigured) {
    return { 
      data: null, 
      error: { 
        message: 'Supabase is not configured. Please connect to Supabase first.',
        status: 400
      } 
    }
  }

  try {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  } catch (error) {
    console.error('Network error during sign in:', error)
    return { 
      data: null, 
      error: { 
        message: 'Network error. Please check your connection and try again.',
        status: 500
      } 
    }
  }
}

export const signOut = async () => {
  if (!isConfigured) {
    return { 
      error: { 
        message: 'Supabase is not configured. Please connect to Supabase first.',
        status: 400
      } 
    }
  }

  try {
    const { error } = await supabase.auth.signOut()
    return { error }
  } catch (error) {
    console.error('Network error during sign out:', error)
    return { 
      error: { 
        message: 'Network error. Please check your connection and try again.',
        status: 500
      } 
    }
  }
}

export const getCurrentUser = async () => {
  if (!isConfigured) {
    return null
  }

  try {
    const { data: { user } } = await supabase.auth.getUser()
    return user
  } catch (error) {
    console.error('Network error getting current user:', error)
    return null
  }
}

export const getUserProfile = async (userId: string): Promise<UserProfile | null> => {
  if (!isConfigured) {
    console.warn('Supabase is not configured. Cannot fetch user profile.')
    return null
  }

  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select(`
        *,
        roles (
          id,
          name,
          description,
          level
        )
      `)
      .eq('id', userId)
      .single()

    if (error) {
      console.error('Error fetching user profile:', error)
      return null
    }

    // Format the data to match our UserProfile interface
    const profile: UserProfile = {
      ...data,
      role_name: data.roles?.name,
      role_level: data.roles?.level
    }

    return profile
  } catch (error) {
    console.error('Error in getUserProfile:', error)
    return null
  }
}

export const updateLastLogin = async (userId: string) => {
  if (!isConfigured) {
    console.warn('Supabase is not configured. Cannot update last login.')
    return
  }

  try {
    const { error } = await supabase.rpc('update_user_last_login', {
      user_uuid: userId
    })
    
    if (error) {
      console.error('Error updating last login:', error)
    }
  } catch (error) {
    console.error('Error in updateLastLogin:', error)
  }
}

export const getUserPermissions = async (userId: string) => {
  if (!isConfigured) {
    console.warn('Supabase is not configured. Cannot fetch user permissions.')
    return []
  }

  try {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      user_uuid: userId
    })
    
    if (error) {
      console.error('Error fetching user permissions:', error)
      return []
    }
    
    return data || []
  } catch (error) {
    console.error('Error in getUserPermissions:', error)
    return []
  }
}

export const hasPermission = async (userId: string, permissionName: string) => {
  if (!isConfigured) {
    console.warn('Supabase is not configured. Cannot check permissions.')
    return false
  }

  try {
    const { data, error } = await supabase.rpc('has_permission', {
      user_uuid: userId,
      permission_name: permissionName
    })
    
    if (error) {
      console.error('Error checking permission:', error)
      return false
    }
    
    return data || false
  } catch (error) {
    console.error('Error in hasPermission:', error)
    return false
  }
}